import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useRef } from "react";

import { usePageTracked } from "~/contexts/page-trackings";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import TrackingUtils from "~/utils/tracking-utils";

import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import CategoryFilters from "./category-filters";
import EditorialElementPLP from "./editorial-element/editorial-element-plp";
import styles from "./product-grid.module.scss";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  components?: Entry<Editorials.GridElement>[];
  listScope: string;
  query?: UseInfiniteQueryResult<any, unknown>;
};

export default function EvaProductListing(props: Props) {
  let countEditorialElement = 0;

  const products = props.query?.data?.pages.flatMap((page) => page.Products ?? []);

  const productsWithEditorialElement: (CrossCountry.Product | Entry<Editorials.GridElement>)[] | undefined = useMemo(
    () => contentfulUtils.mergeProductsWithEditorials<CrossCountry.Product>(products, props.components),
    [products, props.components]
  );

  const isPageTracked = usePageTracked();
  const lastTrackedIndex = useRef(0);
  useEffect(() => {
    lastTrackedIndex.current =
      isPageTracked && products && props.listScope
        ? TrackingUtils.trackPLPImpression(products, lastTrackedIndex.current, props.listScope)
        : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, products]);

  const sentinelRef = useRef(null);

  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });

  // handle infinite scroll
  useEffect(() => {
    if (sentinelInView && props.query?.hasNextPage) {
      props.query.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  return (
    <>
      {productsWithEditorialElement?.map((item, idx) => {
        if (contentfulUtils.isContentful(item)) {
          return (
            <EditorialElementPLP
              editorialElement={item}
              key={item.sys.id}
              countEditorialElement={++countEditorialElement}
            />
          );
        } else {
          return (
            <div key={item.product_id} className={styles.tile}>
              <ProductTile
                product={item}
                idxInList={idx}
                listScope={props.listScope}
                noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
              />
            </div>
          );
        }
      })}
      <div className={styles.sentinelBottom} ref={sentinelRef} aria-hidden="true" />
      <div className={styles.filters}>
        <CategoryFilters pageType="inPLPEva" />
      </div>
    </>
  );
}
